import axios from "axios";
import { getCookie, clearAuthCookies } from "@/utils/authSession";
import {
  getPendingVerifyToken,
  hasPendingVerification,
  setVerifySessionFromApi,
  getVerifySource,
} from "@/utils/pendingVerification";

const api = axios.create({
  baseURL: (process.env.VUE_APP_API_BASE_URL || "/api/v1").replace(/\/$/, ""),
  headers: {
    Accept: "application/json",
  },
});

function getLang() {
  const htmlLang = document.documentElement.lang;
  const stored = localStorage.getItem("app_lang");
  const lang = (htmlLang || stored || "ar").toLowerCase();
  return lang.startsWith("en") ? "en" : "ar";
}

function getAuthToken() {
  return getCookie("auth_token");
}

function applyAuthHeader(config) {
  const authToken = getAuthToken();
  const verifyToken = getPendingVerifyToken();

  config.headers = config.headers || {};
  config.headers["Accept-Language"] = getLang();

  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  } else if (verifyToken) {
    config.headers.Authorization = `Bearer ${verifyToken}`;
  } else {
    delete config.headers.Authorization;
  }

  return config;
}

let verifyRedirectInFlight = false;

// ─── Global 404 handler ────────────────────────────────────────────────────────
// Redirects to the NotFound page for any API 404.
// Individual callers can opt-out by setting config.skipNotFoundRedirect = true
// (useful when a 404 is an expected / handled state, e.g. polling for optional data).
let notFoundRedirectInFlight = false;

function redirectToNotFound() {
  if (notFoundRedirectInFlight) return;
  if (typeof window === "undefined") return;

  // Avoid redirect loops on the 404 page itself
  const current = window.location.pathname;
  if (current === "/404") return;

  notFoundRedirectInFlight = true;

  import("@/router")
    .then(({ default: router }) => {
      router.replace({ name: "NotFound" }).finally(() => {
        notFoundRedirectInFlight = false;
      });
    })
    .catch(() => {
      notFoundRedirectInFlight = false;
    });
}

function redirectToVerifyAccount() {
  if (verifyRedirectInFlight) return;
  if (typeof window === "undefined") return;
  if (window.location.pathname.includes("/verify-account")) return;

  verifyRedirectInFlight = true;

  import("@/router")
    .then(({ default: router }) => {
      router.push({ name: "VerifyAccount" }).finally(() => {
        verifyRedirectInFlight = false;
      });
    })
    .catch(() => {
      verifyRedirectInFlight = false;
    });
}

api.interceptors.request.use((config) => applyAuthHeader(config));

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data || {};
    const config = error?.config || {};

    // ── 404: Resource not found → redirect to /404 page ──────────────────────
    if (status === 404 && !config.skipNotFoundRedirect) {
      redirectToNotFound();
      return Promise.reject(error);
    }

    if (status === 403 && data?.needs_verification) {
      if (!config.skipVerifyRedirect) {
        clearAuthCookies();
        if (data?.token) {
          setVerifySessionFromApi(
            data,
            hasPendingVerification() ? getVerifySource() : "login"
          );
        }
        redirectToVerifyAccount();
      }
    }

    return Promise.reject(error);
  }
);

export default api;
